import { localized, msg } from '@lit/localize';
import { css, html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@spectrum-web-components/tabs/sp-tab.js';
import '@spectrum-web-components/tabs/sp-tab-panel.js';
import '@spectrum-web-components/tabs/sp-tabs.js';

import './email-settings.js';
import './klardaten-settings.js';
import './packages-settings.js';
import './mcp-settings.js';

@customElement('kv-module-settings')
@localized()
export class KvModuleSettings extends LitElement {
  static styles = css`
    :host {
      display: block;
      height: 100%;
      min-height: 0;
      overflow-y: auto;
      padding: 28px;
      background:
        linear-gradient(
          180deg,
          var(--color-sidebar-bg) 0%,
          var(--color-app-bg) 100%
        );
      box-sizing: border-box;
      color: var(--color-app-text);
    }

    .settings-container {
      display: flex;
      flex-direction: column;
      gap: 18px;
      width: min(100%, 1280px);
      max-width: 1280px;
      margin: 0 auto;
    }

    .settings-header {
      display: flex;
      flex-direction: column;
      gap: 6px;
      max-width: 720px;
    }

    .settings-title {
      color: var(--color-app-text);
      font-size: 24px;
      font-weight: 750;
      line-height: 1.15;
      margin: 0;
    }

    .settings-description {
      color: var(--color-app-text-secondary);
      font-size: 13px;
      line-height: 1.45;
      margin: 0;
    }

    .settings-tabs {
      background: var(--color-sidebar-surface);
      border: 1px solid var(--color-app-border);
      border-radius: 8px;
      box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
      padding: 10px 14px 16px;
    }

    sp-tabs {
      width: 100%;
    }

    sp-tab {
      color: var(--color-app-text-secondary);
      font-size: 13px;
    }

    sp-tab[selected] {
      color: var(--color-sidebar-selected-border);
    }

    sp-tab-panel {
      box-sizing: border-box;
      padding-top: 18px;
      width: 100%;
    }

    sp-tab-panel > * {
      width: 100%;
    }

    @media (max-width: 760px) {
      :host {
        padding: 16px 12px;
      }

      .settings-header {
        padding-left: var(--kv-mobile-title-inline-start, 42px);
      }

      .settings-tabs {
        padding: 8px 10px 14px;
      }

      .settings-title {
        font-size: 22px;
      }
    }
  `;

  @state()
  accessor activeTab = 'klardaten';

  private onTabChange = (e: Event) => {
    const target = e.target as EventTarget & { selected?: string };
    const selected = target.selected ?? this.activeTab;
    this.activeTab = selected;
  };

  private onSettingsTabRequested = (e: CustomEvent<{ tab?: string }>): void => {
    const tab = e.detail?.tab;
    if (tab) {
      this.activeTab = tab;
    }
  };

  render() {
    return html`
      <div class="settings-container">
        <header class="settings-header">
          <h1 class="settings-title">${msg('Settings')}</h1>
          <p class="settings-description">
            ${msg('Configure accounts, data connections, packages, and MCP servers.')}
          </p>
        </header>
        <div class="settings-tabs">
          <sp-tabs
            .selected=${this.activeTab}
            @change=${this.onTabChange}
            size="s"
          >
            <sp-tab value="klardaten">Klardaten</sp-tab>
            <sp-tab value="mcp">${msg('MCP Servers')}</sp-tab>
            <sp-tab value="email">${msg('Email')}</sp-tab>
            <sp-tab value="packages">${msg('Packages')}</sp-tab>
            <sp-tab-panel slot="tab-panel" value="klardaten">
              <kv-settings-klardaten></kv-settings-klardaten>
            </sp-tab-panel>
            <sp-tab-panel slot="tab-panel" value="mcp">
              <kv-settings-mcp
                @settings-tab-requested=${this.onSettingsTabRequested}
              ></kv-settings-mcp>
            </sp-tab-panel>
            <sp-tab-panel slot="tab-panel" value="email">
              <kv-settings-email></kv-settings-email>
            </sp-tab-panel>
            <sp-tab-panel slot="tab-panel" value="packages">
              <kv-settings-packages></kv-settings-packages>
            </sp-tab-panel>
          </sp-tabs>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kv-module-settings': KvModuleSettings;
  }
}
